/**
 * Parent Attendance History Page
 *
 * A parent-friendly page showing attendance records for their swimmers:
 * - Summary stats: Total Classes Attended, Attendance Rate, Absences
 * - Filter by student
 * - Timeline view of attendance records grouped by student
 * - Color-coded status badges
 */

import {
  Badge,
  Box,
  Card,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import {
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiClipboard,
  FiMessageSquare,
  FiXCircle,
} from "react-icons/fi"

import { SwimEmptyState } from "@/components/molecules/SwimEmptyState"
import { useAttendances } from "@/hooks/api/useAttendances"
import { useStudents } from "@/hooks/api/useStudents"
import { usePermissions } from "@/hooks/usePermissions"
import type { AttendanceResponse } from "@/types/attendance"

// Status configuration for color-coded badges
const STATUS_CONFIG: Record<
  string,
  { colorPalette: string; label: string }
> = {
  present: { colorPalette: "green", label: "Present" },
  absent: { colorPalette: "red", label: "Absent" },
  late: { colorPalette: "yellow", label: "Late" },
  excused: { colorPalette: "blue", label: "Excused" },
  makeup: { colorPalette: "purple", label: "Makeup" },
  cancelled: { colorPalette: "gray", label: "Cancelled" },
}

// Format date for display
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Status Badge Component
function StatusBadge({ status }: { status: string | null }) {
  const config = STATUS_CONFIG[status || ""] || {
    colorPalette: "gray",
    label: status || "Unknown",
  }

  return (
    <Badge
      colorPalette={config.colorPalette}
      variant="subtle"
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="semibold"
    >
      {config.label}
    </Badge>
  )
}

// Attendance Record Card Component
function AttendanceRecordCard({
  attendance,
}: {
  attendance: AttendanceResponse
}) {
  const status = attendance.status || "present"
  // Pick a left-border color based on status
  const borderColorMap: Record<string, string> = {
    present: "green.400",
    absent: "red.400",
    late: "yellow.400",
    excused: "blue.400",
    makeup: "purple.400",
    cancelled: "gray.400",
  }
  const leftBorderColor = borderColorMap[status] || "gray.400"

  return (
    <Card.Root
      bg="white"
      borderRadius="lg"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      borderLeftWidth="4px"
      borderLeftColor={leftBorderColor}
      _hover={{ boxShadow: "md" }}
      transition="all 0.2s"
    >
      <Card.Body p={4}>
        <VStack align="stretch" gap={3}>
          {/* Top row: Date and Status */}
          <Flex justify="space-between" align="center">
            <HStack gap={2}>
              <Icon color="gray.400" fontSize="sm">
                <FiCalendar />
              </Icon>
              <Text fontWeight="medium" color="gray.800" fontSize="sm">
                {formatDate(attendance.class_date)}
              </Text>
              {attendance.class_time && (
                <Text color="gray.500" fontSize="sm">
                  at {attendance.class_time}
                </Text>
              )}
            </HStack>
            <StatusBadge status={attendance.status} />
          </Flex>

          {/* Class name */}
          <Text fontWeight="semibold" color="gray.800">
            {attendance.class_name || "Swim Class"}
          </Text>

          {/* Student name (shown when viewing all students) */}
          {attendance.student_name && (
            <Text fontSize="sm" color="gray.600">
              Swimmer: {attendance.student_name}
            </Text>
          )}

          {/* Instructor notes */}
          {attendance.instructor_notes && (
            <Box bg="gray.50" borderRadius="md" p={3}>
              <HStack align="start" gap={2}>
                <Icon color="ui.main" fontSize="sm" mt={0.5}>
                  <FiMessageSquare />
                </Icon>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                    Coach Notes
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    {attendance.instructor_notes}
                  </Text>
                </Box>
              </HStack>
            </Box>
          )}

          {/* Behavior notes */}
          {attendance.behavior_notes && (
            <Box bg="blue.50" borderRadius="md" p={3}>
              <HStack align="start" gap={2}>
                <Icon color="blue.500" fontSize="sm" mt={0.5}>
                  <FiClipboard />
                </Icon>
                <Box>
                  <Text fontSize="xs" color="blue.600" fontWeight="medium" mb={1}>
                    Behavior Notes
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    {attendance.behavior_notes}
                  </Text>
                </Box>
              </HStack>
            </Box>
          )}

          {/* Makeup class indicator */}
          {attendance.is_makeup_class && (
            <Badge colorPalette="purple" variant="outline" alignSelf="flex-start">
              Makeup Class
            </Badge>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

// Student filter button
function StudentFilterButton({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      px={4}
      py={2}
      borderRadius="full"
      fontSize="sm"
      fontWeight="medium"
      bg={isActive ? "ui.main" : "white"}
      color={isActive ? "white" : "gray.700"}
      borderWidth="1px"
      borderColor={isActive ? "ui.main" : "gray.200"}
      _hover={{
        bg: isActive ? "ui.mainHover" : "gray.50",
        borderColor: isActive ? "ui.mainHover" : "gray.300",
      }}
      transition="all 0.2s"
      cursor="pointer"
      whiteSpace="nowrap"
    >
      {label}
    </Box>
  )
}

export default function ParentAttendanceHistory() {
  const { hasPermission } = usePermissions()
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  )

  // Check permission
  const canViewAttendance =
    hasPermission("attendances:read") ||
    hasPermission("attendances:read_own") ||
    hasPermission("attendances:read_assigned")

  // Fetch students
  const { data: studentsData, isLoading: studentsLoading } = useStudents()
  const students = studentsData?.items || []

  // Fetch attendance records (with optional student filter)
  const { data: attendancesData, isLoading: attendancesLoading } =
    useAttendances({
      limit: 500,
      sort_by: "created_at",
      sort_order: "desc",
      ...(selectedStudentId ? { student_id: selectedStudentId } : {}),
    })

  const attendances = attendancesData?.items || []

  // Sort attendances by class_date descending (most recent first)
  const sortedAttendances = useMemo(() => {
    return [...attendances].sort((a, b) => {
      const dateA = new Date(a.class_date).getTime()
      const dateB = new Date(b.class_date).getTime()
      return dateB - dateA
    })
  }, [attendances])

  // Calculate stats
  const stats = useMemo(() => {
    const total = attendances.length
    const present = attendances.filter(
      (a) => a.status === "present" || a.status === "late",
    ).length
    const absent = attendances.filter((a) => a.status === "absent").length
    const rate = total > 0 ? Math.round((present / total) * 100) : 0

    return { total, present, absent, rate }
  }, [attendances])

  const isLoading = studentsLoading || attendancesLoading

  if (!canViewAttendance) {
    return (
      <Container maxW="6xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
        <VStack gap={4} py={16}>
          <Text color="gray.500">
            You do not have permission to view attendance records.
          </Text>
        </VStack>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container maxW="6xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
        <VStack gap={4} py={16}>
          <Spinner size="xl" color="ui.main" />
          <Text color="gray.500">Loading attendance history...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="6xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
      <VStack gap={{ base: 4, md: 8 }} align="stretch">
        {/* Header with back link */}
        <Box>
          <Link to="/" style={{ textDecoration: "none" }}>
            <HStack
              color="ui.main"
              fontSize="sm"
              fontWeight="medium"
              mb={3}
              _hover={{ color: "ui.mainHover" }}
              cursor="pointer"
            >
              <FiArrowLeft />
              <Text>Back to Dashboard</Text>
            </HStack>
          </Link>
          <Heading size={{ base: "lg", md: "xl" }} color="gray.800" mb={2}>
            Attendance History
          </Heading>
          <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
            Track your swimmers' attendance and see coach feedback
          </Text>
        </Box>

        {/* Stats Summary */}
        <SimpleGrid columns={{ base: 1, sm: 3 }} gap={{ base: 3, md: 4 }}>
          {/* Total Classes Attended */}
          <Card.Root bg="ui.backgroundAlt" borderRadius="xl">
            <Card.Body p={5}>
              <HStack>
                <Box p={3} bg="white" borderRadius="lg">
                  <Icon color="ui.main" fontSize="xl">
                    <FiCheckCircle />
                  </Icon>
                </Box>
                <Box>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    {stats.present}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Classes Attended
                  </Text>
                </Box>
              </HStack>
            </Card.Body>
          </Card.Root>

          {/* Attendance Rate */}
          <Card.Root
            bg={stats.rate >= 80 ? "green.50" : stats.rate >= 60 ? "yellow.50" : "red.50"}
            borderRadius="xl"
          >
            <Card.Body p={5}>
              <HStack>
                <Box p={3} bg="white" borderRadius="lg">
                  <Icon
                    color={stats.rate >= 80 ? "green.500" : stats.rate >= 60 ? "yellow.500" : "red.500"}
                    fontSize="xl"
                  >
                    <FiClipboard />
                  </Icon>
                </Box>
                <Box>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    {stats.rate}%
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Attendance Rate
                  </Text>
                </Box>
              </HStack>
            </Card.Body>
          </Card.Root>

          {/* Absences */}
          <Card.Root
            bg={stats.absent > 0 ? "orange.50" : "green.50"}
            borderRadius="xl"
          >
            <Card.Body p={5}>
              <HStack>
                <Box p={3} bg="white" borderRadius="lg">
                  <Icon
                    color={stats.absent > 0 ? "orange.500" : "green.500"}
                    fontSize="xl"
                  >
                    <FiXCircle />
                  </Icon>
                </Box>
                <Box>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    {stats.absent}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {stats.absent === 1 ? "Absence" : "Absences"}
                  </Text>
                </Box>
              </HStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Student Filter */}
        {students.length > 1 && (
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
              Filter by Swimmer
            </Text>
            <Flex gap={2} flexWrap="wrap">
              <StudentFilterButton
                label="All Swimmers"
                isActive={selectedStudentId === null}
                onClick={() => setSelectedStudentId(null)}
              />
              {students.map((student) => (
                <StudentFilterButton
                  key={student.id}
                  label={`${student.first_name} ${student.last_name}`}
                  isActive={selectedStudentId === student.id}
                  onClick={() => setSelectedStudentId(student.id)}
                />
              ))}
            </Flex>
          </Box>
        )}

        {/* Attendance Timeline */}
        <Box>
          <Heading size="md" color="gray.700" mb={4}>
            {selectedStudentId
              ? `Attendance for ${students.find((s) => s.id === selectedStudentId)?.first_name || "Student"}`
              : "All Attendance Records"}
          </Heading>

          {sortedAttendances.length === 0 ? (
            <SwimEmptyState
              resource="attendance"
              title="No attendance records yet"
              description="Attendance records will appear here as your swimmers attend their classes. Check back after their next lesson!"
              size="md"
            />
          ) : (
            <VStack gap={3} align="stretch">
              {sortedAttendances.map((attendance) => (
                <AttendanceRecordCard
                  key={attendance.id}
                  attendance={attendance}
                />
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </Container>
  )
}
